/**
 * @file ShipmentSheetSummary.tsx
 * @description Секции «Получатель», «Куда» и «Доставка» сайдбара новой
 * отгрузки (2026-09-22). Только чтение согласованного: перевозчику нужен
 * человек с телефоном, точка и тариф — менеджер видит их одним взглядом и
 * может сменить ПВЗ, не уходя из сайдбара.
 */

import { MapPin, Phone, UserRound } from "lucide-react";

import { usePickupPoint } from "../carriers/usePickupPoint";
import type { OrderDeliveryReceiptDto } from "../../lib/api-delivery-agreement";
import type { OrderDeliveryRecipientDto } from "../../lib/api-orders";
import { formatPeriod, formatRub } from "../../lib/delivery-quote";
import { formatPhone } from "../../lib/phone-format";
import { formatShopDateTime } from "../../lib/shop-time";
import { Button } from "../ui/button";
import { SectionLabel } from "../ui/SectionLabel";

interface Props {
  recipient: OrderDeliveryRecipientDto | null;
  receipt: OrderDeliveryReceiptDto;
  carrierName: string;
  /** Код ПВЗ, выбранный в сайдбаре (может отличаться от согласованного). */
  pickupPointCode: string | null;
  canChangePickup: boolean;
  onChangePickup: () => void;
}

export function ShipmentSheetSummary({
  recipient,
  receipt,
  carrierName,
  pickupPointCode,
  canChangePickup,
  onChangePickup,
}: Props) {
  const { point } = usePickupPoint(pickupPointCode, receipt.city);
  const quote = receipt.quote ?? null;
  const quotedAddress = quote?.destination.address ?? null;
  const agreedPvz = quote?.destination.pickupPointCode ?? receipt.pickupPointCode ?? null;
  const pvzChanged = !!pickupPointCode && !!agreedPvz && pickupPointCode !== agreedPvz;

  return (
    <>
      <section aria-label="Получатель" className="flex flex-col gap-1">
        <SectionLabel>Получатель</SectionLabel>
        <div className="flex items-center gap-2 text-sm">
          <UserRound className="size-4 shrink-0 text-on-surface-variant" aria-hidden />
          <span className="font-medium">{recipient?.name?.trim() || "Имя не заполнено"}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-on-surface-variant">
          <Phone className="size-4 shrink-0" aria-hidden />
          <span>{recipient?.phone?.trim() ? formatPhone(recipient.phone) : "Телефон не заполнен"}</span>
          {recipient?.email ? <span>· {recipient.email}</span> : null}
        </div>
      </section>

      <section aria-label="Куда" className="flex flex-col gap-1">
        <SectionLabel>Куда</SectionLabel>
        <div className="flex items-start gap-2 text-sm">
          <MapPin className="mt-0.5 size-4 shrink-0 text-on-surface-variant" aria-hidden />
          <div className="min-w-0 flex-1">
            <div className="font-medium">
              {carrierName}
              {pickupPointCode ? ` · ПВЗ ${pickupPointCode}` : " · курьером до двери"}
              {pvzChanged ? (
                <span className="ml-2 text-xs text-on-surface-variant">
                  (согласован {agreedPvz})
                </span>
              ) : null}
            </div>
            {/* Адрес пункта — из кэша ПВЗ, а не из строки заказа: там лежит
                техническая склейка «Город, ПВЗ КОД». */}
            <div className="text-on-surface-variant">
              {point?.address ?? quotedAddress ?? receipt.address ?? "Адрес не указан"}
            </div>
            {point?.workingHours ? (
              <div className="text-xs text-on-surface-variant">{point.workingHours}</div>
            ) : null}
          </div>
          {canChangePickup ? (
            <Button variant="outline" size="sm" className="min-h-11 shrink-0" onClick={onChangePickup}>
              Сменить ПВЗ
            </Button>
          ) : null}
        </div>
      </section>

      <section aria-label="Доставка" className="flex flex-col gap-1">
        <SectionLabel>Доставка</SectionLabel>
        {quote ? (
          <div className="text-sm">
            <span className="font-medium">
              {quote.tariffName} ({quote.tariffCode})
            </span>
            {receipt.deliveryAmount ? ` · ${formatRub(Number(receipt.deliveryAmount))}` : ""}
            {` · ${formatPeriod(quote.periodMin ?? null, quote.periodMax ?? null)}`}
            <div className="text-xs text-on-surface-variant">
              рассчитано {formatShopDateTime(quote.quotedAt)}
            </div>
          </div>
        ) : (
          <div className="text-sm text-on-surface-variant">
            Тариф не рассчитан — перевозчик назначит его сам
            {receipt.deliveryAmount
              ? `; с клиента согласовано ${formatRub(Number(receipt.deliveryAmount))}`
              : ""}
            .
          </div>
        )}
      </section>
    </>
  );
}
